import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { adminRequest, type ApiTarget } from "../api/request.js";
import { commitBrowserKey, forgetBrowserKey, prepareBrowserKey } from "./browser-key.js";
import { clearAdminSession, writeAdminSession, type StoredAdminSession } from "./session-store.js";

/**
 * The admin sign-in, start to end (docs/product/admin-auth.md §2–§3). Separate from the dashboard's in every part: its
 * own routes (`/v1/admin/session…`), its own text ("TEnergy admin sign-in", the site, this browser's key thumbprint),
 * its own table of people and sessions. Proving an address works the same way as anywhere — a wallet signature — but
 * it is a different sign-in.
 *
 * Each sign-in makes a FRESH browser key first and binds it into the signed text, so the session it opens can only be
 * used by this browser.
 */

interface SessionAnswer {
  session: string;
  address: string;
  role: StoredAdminSession["role"];
  method: StoredAdminSession["method"];
  expires_at: string;
}

async function keep(target: ApiTarget, answer: SessionAnswer): Promise<StoredAdminSession> {
  await commitBrowserKey(target);
  const stored: StoredAdminSession = {
    token: answer.session,
    address: answer.address,
    role: answer.role,
    method: answer.method,
    expiresAt: answer.expires_at,
  };
  writeAdminSession(target, stored);
  return stored;
}

/** A wallet (TRON or Ethereum) signs the server's sign-in text; `sign` is TronLink, WalletConnect or a Ledger. */
export async function signInWithWallet(
  target: ApiTarget,
  address: string,
  sign: (text: string) => Promise<string>,
): Promise<StoredAdminSession> {
  const browserKey = await prepareBrowserKey(target);
  const challenge = await adminRequest<{ challenge: string; message: string }>("/admin/session/challenge", {
    method: "POST",
    body: { address, browser_key: browserKey },
    target,
    anonymous: true,
  });
  const signature = await sign(challenge.message);
  const answer = await adminRequest<SessionAnswer>("/admin/session", {
    method: "POST",
    body: { challenge: challenge.challenge, signature },
    target,
    anonymous: true,
  });
  return await keep(target, answer);
}

/**
 * A passkey bound to the address (owner: equal to that wallet's signature). Non-discoverable, so the address comes
 * first and the key is asked by its credential id — Ledger's Security Key app keeps no credential on the device.
 */
export async function signInWithPasskey(target: ApiTarget, address: string): Promise<StoredAdminSession> {
  const browserKey = await prepareBrowserKey(target);
  const { challenge_id, options } = await adminRequest<{ challenge_id: string; options: Parameters<typeof startAuthentication>[0]["optionsJSON"] }>(
    "/admin/session/passkey/options",
    { method: "POST", body: { address, browser_key: browserKey }, target, anonymous: true },
  );
  const response = await startAuthentication({ optionsJSON: options });
  const answer = await adminRequest<SessionAnswer>("/admin/session/passkey", {
    method: "POST",
    body: { challenge_id, response },
    target,
    anonymous: true,
  });
  return await keep(target, answer);
}

/**
 * The Nile page opened from a mainnet session, without a second sign-in (owner 2026-11-03: «до изменений авторизации
 * автоматически входило вообще-то»). A fresh Nile browser key, a 60 s pass from mainnet naming it, and Nile opens its
 * own session for that key with the role ITS admin table gives the address.
 */
export async function signInToNileFromMainnet(): Promise<StoredAdminSession> {
  const browserKey = await prepareBrowserKey("nile");
  const pass = await adminRequest<{ token: string }>("/admin/session/handoff", {
    method: "POST",
    body: { browser_key: browserKey },
    target: "mainnet",
  });
  const answer = await adminRequest<SessionAnswer>("/admin/session/handoff/accept", {
    method: "POST",
    body: { token: pass.token },
    target: "nile",
    anonymous: true,
  });
  return await keep("nile", answer);
}

export async function signOutOf(target: ApiTarget): Promise<void> {
  await adminRequest("/admin/session", { method: "DELETE", target }).catch(() => undefined);
  clearAdminSession(target);
  await forgetBrowserKey(target).catch(() => undefined);
}

/** Bind a new passkey to the signed-in address (the session must have been opened by a wallet signature). */
export async function addPasskey(label: string): Promise<void> {
  const { challenge_id, options } = await adminRequest<{ challenge_id: string; options: Parameters<typeof startRegistration>[0]["optionsJSON"] }>(
    "/admin/passkeys/options",
    { method: "POST", body: {} },
  );
  const response = await startRegistration({ optionsJSON: options });
  await adminRequest("/admin/passkeys", { method: "POST", body: { challenge_id, response, label } });
}
